import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plan } from '../../types';
import { useData } from '../../context/DataContext';
import { useSincronizacionAMedias } from '../../context/SincronizacionAMediasContext';
import { useSincronizacionPlan } from '../../hooks/useSincronizacionPlan';
import { useAvisosAMedias } from '../../context/AvisosAMediasContext';
import { totalCambios } from '../../utils/sincronizacionAMedias';
import { Campo, claseInput, MensajeError, ModalBase, BotonesModal } from './ui';

interface Props {
  plan: Plan;
  sync: ReturnType<typeof useSincronizacionPlan>;
  onClose: () => void;
}

/** Elige la categoría más probable para los gastos de un plan. */
const categoriaSugerida = (categorias: { id: string; nombre: string }[]) =>
  categorias.find(c => /viaj|vacac|ocio|restaur|comida/i.test(c.nombre))?.id ?? categorias[0]?.id ?? '';

export const ModalSincronizacion: React.FC<Props> = ({ plan, sync, onClose }) => {
  const { cuentas, categorias } = useData();
  const { guardarSincronizacion } = useSincronizacionAMedias();
  const { reintentarFinanzas } = useAvisosAMedias();
  const categoriasGasto = useMemo(
    () => categorias.filter(c => c.tipo === 'gasto').sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')),
    [categorias]);

  const c = sync.config;
  const [activa, setActiva] = useState(c ? c.activa : true);
  const [cuentaId, setCuentaId] = useState(
    c && cuentas.some(x => x.id === c.cuentaId) ? c.cuentaId : cuentas.find(x => x.tipo === 'corriente')?.id ?? cuentas[0]?.id ?? '');
  const [categoriaId, setCategoriaId] = useState(
    c && categoriasGasto.some(x => x.id === c.categoriaId) ? c.categoriaId : categoriaSugerida(categoriasGasto));
  const [automatica, setAutomatica] = useState(c?.automatica ?? false);
  const [quitar, setQuitar] = useState(false);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const yaActiva = !!c?.activa;
  const porPasar = totalCambios(sync.todas);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (activa && !cuentaId) { setError('Elige la cuenta.'); return; }
    if (activa && !categoriaId) { setError('Elige la categoría de tu parte.'); return; }
    setGuardando(true);
    setError('');
    try {
      if (!activa && quitar) await sync.quitarDeFinanzas();
      await guardarSincronizacion(plan.id, { activa, cuentaId, categoriaId, automatica });
      sync.reintentar();
      reintentarFinanzas(plan.id);
      onClose();
    } catch (err) {
      console.error(err);
      setError('No se pudo guardar. Inténtalo de nuevo.');
      setGuardando(false);
    }
  };

  if (cuentas.length === 0 || categoriasGasto.length === 0) {
    return (
      <ModalBase titulo="Pasar a Finanzas" onClose={onClose}>
        <div className="p-5 space-y-3 text-sm text-gray-700 dark:text-gray-300">
          <p>Para pasar tus gastos del plan a Finanzas necesitas al menos una cuenta y una categoría de gasto.</p>
          <Link to="/configuracion" className="inline-block font-semibold text-emerald-700 dark:text-emerald-400 hover:underline">
            Ir a Configuración
          </Link>
        </div>
      </ModalBase>
    );
  }

  return (
    <ModalBase titulo="Pasar a Finanzas" onClose={onClose}>
      <form onSubmit={enviar} className="p-5 space-y-4">
        <MensajeError texto={error} />

        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={activa} onChange={e => setActiva(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-emerald-600" />
          <span className="text-sm">
            <span className="font-semibold text-gray-900 dark:text-white">Pasar mis movimientos de este plan a Finanzas</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Solo afecta a tus Finanzas; los demás deciden por su cuenta.
            </span>
          </span>
        </label>

        {activa && (
          <>
            <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800
                            p-3 text-xs text-emerald-900 dark:text-emerald-200 space-y-1">
              <p><span className="font-semibold">Tu parte</span> de cada gasto entra como gasto real en la categoría que elijas.</p>
              <p><span className="font-semibold">Lo que adelantas</span> por los demás, o te adelantan, entra como adelanto
                con «{plan.nombre}» de persona: lo verás pendiente en el Dashboard hasta que saldéis.</p>
              <p>Los <span className="font-semibold">pagos</span> que hagas o recibas en el plan cierran ese pendiente.</p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Campo label="Cuenta" ayuda="Desde la que pagas. Puedes cambiarla en cada movimiento.">
                <select value={cuentaId} onChange={e => setCuentaId(e.target.value)} className={claseInput}>
                  {cuentas.map(x => <option key={x.id} value={x.id}>{x.icono ? `${x.icono} ` : ''}{x.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Categoría de tu parte">
                <select value={categoriaId} onChange={e => setCategoriaId(e.target.value)} className={claseInput}>
                  {categoriasGasto.map(x => <option key={x.id} value={x.id}>{x.icono} {x.nombre}</option>)}
                </select>
              </Campo>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={automatica} onChange={e => setAutomatica(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-emerald-600" />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Pasar sin preguntar también lo que apunten otros
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  Si no, te lo enseñamos antes para que lo confirmes. Lo que apuntas tú se pasa siempre solo.
                </span>
              </span>
            </label>

            {!yaActiva && porPasar > 0 && (
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Con lo que ya hay en el plan se crearán {sync.todas.crear.length} movimientos en Finanzas.
              </p>
            )}
          </>
        )}

        {!activa && yaActiva && sync.generadas.length > 0 && (
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={quitar} onChange={e => setQuitar(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-red-600" />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Quitar también los {sync.generadas.length} movimientos que ya pasaron a Finanzas
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Si no, se quedan como están y dejan de actualizarse.
              </span>
            </span>
          </label>
        )}

        <BotonesModal guardando={guardando} texto="Guardar" onClose={onClose} />
      </form>
    </ModalBase>
  );
};
